import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage from '../../components/shell/RecordPage';
import { Panel, StatTiles } from '../../components/shell/Page';
import { Users, Books, ChartLineUp, GraduationCap, AddressBook } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['hsl(var(--shell-primary))', 'hsl(36, 92%, 44%)', 'hsl(215, 16%, 47%)', 'hsl(214, 14%, 75%)'];

export default function TenantAdminDashboard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [enrollmentReport, setEnrollmentReport] = useState({ by_course: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, enrollmentRes] = await Promise.all([
        api.get('/reports/dashboard'),
        api.get('/reports/enrollments')
      ]);
      setStats(statsRes.data);
      setEnrollmentReport(enrollmentRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const completionData = [
    { name: 'Completed', value: stats.completed_enrollments || 0 },
    { name: 'In Progress', value: (stats.total_enrollments || 0) - (stats.completed_enrollments || 0) },
  ];

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="tenant-admin-dashboard"
        title="Dashboard"
        subtitle="Welcome back! Here's your organization's overview."
        onBack={null}
        findable={false}
        onRefresh={fetchData}
        refreshing={loading}
        loading={loading}
        maxWidth="none"
        primary={[
          { key: 'users', label: 'Users', icon: Users, variant: 'outline', onClick: () => navigate('/admin/users') },
          { key: 'courses', label: 'Courses', icon: Books, variant: 'outline', onClick: () => navigate('/admin/courses') },
          { key: 'crm', label: 'CRM', icon: AddressBook, variant: 'outline', onClick: () => navigate('/crm') },
        ]}
      >
        {/* KPIs */}
        <StatTiles items={[
          { label: 'Total users', value: stats.total_users || 0, icon: Users },
          { label: 'Total courses', value: stats.total_courses || 0, icon: Books, iconClass: 'bg-amber-100 text-amber-700' },
          { label: 'Published courses', value: stats.published_courses || 0, icon: GraduationCap, iconClass: 'bg-sky-100 text-sky-700' },
          { label: 'Total enrollments', value: stats.total_enrollments || 0, icon: ChartLineUp, iconClass: 'bg-emerald-100 text-emerald-700' },
        ]} />

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Enrollment by Course */}
          <Panel title="Enrollments by course">
            {enrollmentReport.by_course.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={enrollmentReport.by_course.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="course_title"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="total" fill="hsl(var(--shell-primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No enrollment data yet
              </div>
            )}
          </Panel>

          {/* Completion Rate */}
          <Panel title="Completion rate">
            {stats.total_enrollments > 0 ? (
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={completionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {completionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No completion data yet
              </div>
            )}
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[0] }} />
                <span className="text-sm">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[1] }} />
                <span className="text-sm">In Progress</span>
              </div>
            </div>
          </Panel>
        </div>
      </RecordPage>
    </DashboardLayout>
  );
}
